import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getSegmentOrDomainCellEnrichedAttributes } from "app/components/React/Table/cells/SegmentOrDomainCell";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { SUB_CATEGORY_DELIMITER, CATEGORY_PARENT_AND_NAME_DELIMITER } from "app/common/services/categoryService";
import { enrichRecordsWithRankSignals, mapSiteToMover } from "./website-rankings-momentum/rankSignals";
import type {
    ICategoryLeadersResponse,
    ISite,
    ITopMover,
    ITopMovers,
} from "./website-rankings-momentum/websiteRankings.types";

// Derived from the (already-imported) SegmentsUtils signature so we stay within the boundary
// zone — importing ISegmentsModule directly crosses the app-shared -> segments boundary.
type SegmentsModule = Parameters<typeof SegmentsUtils.getUserAndAccountSegmentByIdHof>[0];

export const transformDataHof = (segmentsModule: SegmentsModule) => (rawData: ICategoryLeadersResponse) => {
    const params = proNavigator.getParams();
    const getUserAndAccountSegmentById = SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule);
    const getDomainUrl = (domain: string): string =>
        proNavigator.href("digitalsuite_website_websiteperformance", {
            key: domain,
            isWWW: "*",
            ...params,
        });
    const records = rawData.Data.map((record) => {
        const { Domain: domain, Id: SegmentId, Category } = record;
        const segment = getUserAndAccountSegmentById(SegmentId);
        const segmentOrDomainCellEnrichedAttributes = getSegmentOrDomainCellEnrichedAttributes(record, segment);
        return {
            ...record,
            ...segmentOrDomainCellEnrichedAttributes,
            url: getDomainUrl(domain),
            Category: Category
                ? Category.split(SUB_CATEGORY_DELIMITER).join(CATEGORY_PARENT_AND_NAME_DELIMITER)
                : Category,
        };
    });
    const toMovers = (sites: ISite[] = []): ITopMover[] =>
        sites.map((site) => mapSiteToMover(site, getDomainUrl(site.Domain)));
    const topMovers: ITopMovers = {
        climbers: toMovers(rawData.TopClimbers),
        fallers: toMovers(rawData.TopFallers),
        newEntrants: toMovers(rawData.NewEntrants),
    };
    return {
        ...rawData,
        records: enrichRecordsWithRankSignals(records),
        topMovers,
    };
};
